// Templates strip — 4 hardcoded preview tiles. We deliberately don't fetch
// /api/projects/templates here so the marketing page stays static and free
// of loading states. Cards link into the gallery.

import { appHref } from "@/components/home/links";

interface Tile {
  name: string;
  sub: string;
  nodes: number;
  badge: string | null;
  preview: Array<[number, number]>;
}

const TILES: Tile[] = [
  {
    name: "Hello Harness",
    sub: "2 agents, 1 tool — start here",
    nodes: 3,
    badge: "STARTER",
    preview: [
      [20, 40],
      [80, 40],
      [140, 40],
    ],
  },
  {
    name: "Feature Factory",
    sub: "PRD → reviewed PR",
    nodes: 11,
    badge: "POPULAR",
    preview: [
      [14, 30],
      [60, 30],
      [106, 14],
      [106, 50],
      [152, 30],
    ],
  },
  {
    name: "Support Triage",
    sub: "Inbound ticket → routed reply",
    nodes: 7,
    badge: null,
    preview: [
      [14, 14],
      [60, 30],
      [60, 50],
      [106, 30],
    ],
  },
  {
    name: "Research Brief",
    sub: "Question → annotated brief",
    nodes: 6,
    badge: null,
    preview: [
      [14, 30],
      [60, 14],
      [60, 50],
      [106, 30],
    ],
  },
];

export default function HomeTemplates() {
  return (
    <section
      id="templates"
      className="mx-auto max-w-[1320px] border-t border-border px-7 py-16"
    >
      <div className="mb-[22px] flex items-baseline justify-between">
        <div>
          <div className="mb-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
            03 — Start from a template
          </div>
          <h2 className="serif m-0 text-[44px] font-normal leading-none tracking-[-0.02em] text-ink">
            Real harnesses, ready to fork.
          </h2>
        </div>
        <a
          href={appHref("/templates")}
          className="inline-flex items-center gap-1.5 text-[13px] text-ink-2"
        >
          Browse all templates
          <span className="opacity-50">↗</span>
        </a>
      </div>

      <div className="grid grid-cols-4 gap-3.5">
        {TILES.map((it) => (
          <a
            key={it.name}
            href={appHref("/templates")}
            className="flex min-h-[220px] flex-col gap-3.5 rounded-[14px] border border-border bg-surface p-4 text-inherit no-underline"
          >
            <div
              className="relative min-h-[100px] flex-1 rounded-lg border border-border bg-canvas"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(20,20,30,0.10) 1px, transparent 1px)",
                backgroundSize: "14px 14px",
              }}
            >
              <TilePreview dots={it.preview} />
            </div>
            <div className="flex items-baseline gap-2">
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-ink">
                  {it.name}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted">{it.sub}</div>
              </div>
              {it.badge && (
                <span
                  className="rounded-sm px-1.5 py-0.5 font-mono text-[9px] font-semibold tracking-[0.10em]"
                  style={{
                    background: "var(--accent-soft)",
                    color: "var(--accent-deep)",
                  }}
                >
                  {it.badge}
                </span>
              )}
            </div>
            <div className="flex justify-between border-t border-border pt-2 font-mono text-[10px] tracking-[0.04em] text-muted">
              <span>{it.nodes} nodes</span>
              <span>fork →</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function TilePreview({ dots }: { dots: Array<[number, number]> }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 180 80"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      {dots.map(([x, y], i) => {
        const prev = i > 0 ? dots[i - 1] : null;
        return (
          <g key={i}>
            {prev && (
              <line
                x1={prev[0] + 10}
                y1={prev[1] + 5}
                x2={x}
                y2={y + 5}
                stroke="rgba(20,20,30,0.3)"
                strokeWidth="1"
              />
            )}
            <rect
              x={x}
              y={y}
              width="10"
              height="10"
              rx="2"
              fill="var(--accent-soft)"
              stroke="var(--accent)"
              strokeWidth="1"
            />
          </g>
        );
      })}
    </svg>
  );
}
